// ABOUTME: One job opened from the feed: who and what, the description and requirements, then the contract, the wage,
// ABOUTME: the hours, the places, the interview and the process, and the apply footer at the bottom. The
// ABOUTME: header and the sections (without the process) render on their own too, for an application and my job.
import { ReactNode, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { Iconify } from 'modules/minimal-ui-theme';
import { useJobListing, useListingEngagement } from '../hooks';
import { hasListingText } from '../listingText';
import { JobDetails } from '../types';
import {
  contractLength,
  contractLengthLabel,
  dayRangeLabel,
  formatClock,
  formatDay,
  formatMoney,
  groupWindows,
  hoursLabel,
  initialsOf,
  salaryQualifierKey,
  weekendDaysLabel
} from '../utils';
import { ApplyFooter } from './ApplyFooter';
import { JobPageRow as Row, JobPageSection as Section } from './JobPageParts';
import { ListingBadges } from './ListingBadges';
import {
  LocationCheckNotice,
  Locations,
  OpenEndedNotice,
  ProcessSteps,
  WeekendWorkBlock,
  WorkingTimeNotice
} from './ListingDetailSections';
import { ListingText } from './ListingText';

const Prose = ({ text }: { text: string }) => (
  <Text
    variant="body2"
    color="text.primary"
    component="p"
    sx={{ whiteSpace: 'pre-line' }}
  >
    {text}
  </Text>
);

/**
 * Start, end or open-endedness, and the length. A listing always says whether it is open-ended; the job of an
 * application or of my job may not, and then only a known end is shown. Nothing known, no section.
 */
const Contract = ({ listing }: { listing: JobDetails }) => {
  const { startDate, endDate, unlimitedContract, minContractMonths } = listing;
  const length = startDate ? contractLength(startDate, endDate) : undefined;
  const openEnded =
    unlimitedContract === true || (unlimitedContract === false && !endDate);
  if (!startDate && !endDate && !openEnded) return null;
  return (
    <Section
      title={getTrans('jobs.detail.contract')}
      testId="job-detail-contract"
    >
      {unlimitedContract && <OpenEndedNotice />}
      {startDate && (
        <Row
          label={getTrans('jobs.detail.start')}
          value={formatDay(startDate)}
        />
      )}
      {openEnded && (
        <Row
          label={getTrans('jobs.detail.length')}
          value={
            minContractMonths
              ? getTransWithParams('jobs.detail.openEndedMin', {
                  count: minContractMonths
                })
              : getTrans('jobs.detail.unlimited')
          }
        />
      )}
      {!openEnded && endDate && (
        <Row label={getTrans('jobs.detail.end')} value={formatDay(endDate)} />
      )}
      {length && !openEnded && (
        <Row
          label={getTrans('jobs.detail.length')}
          value={contractLengthLabel(length)}
        />
      )}
    </Section>
  );
};

/** "Vorher 16,50 € · JETZT +1,00 €": the raise, for a talent who saw an earlier version of the listing. */
const RaisedRow = ({
  listing
}: {
  listing: JobDetails & { hourlyRate: number };
}) => {
  if (!listing.earningsRaised) return null;
  const { previousHourlyRate } = listing.earningsRaised;
  return (
    <Box
      data-testid="job-detail-raised"
      sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}
    >
      <Text variant="body2" color="text.secondary">
        {getTransWithParams('jobs.detail.previousRate', {
          amount: formatMoney(previousHourlyRate)
        })}
      </Text>
      <Text variant="body2" color="success.dark" sx={{ fontWeight: 700 }}>
        {getTransWithParams('jobs.detail.raisedBy', {
          amount: formatMoney(listing.hourlyRate - previousHourlyRate)
        })}
      </Text>
    </Box>
  );
};

/**
 * The monthly figure as far as it is known: "bis zu" or "fest" with both ends, the bare figure with a lone maximum,
 * "ab" with a lone minimum; nothing without either.
 */
const monthlyFigureOf = ({
  monthlySalaryMin,
  monthlySalaryMax
}: JobDetails): string | undefined => {
  if (monthlySalaryMax === undefined) {
    return monthlySalaryMin === undefined
      ? undefined
      : getTransWithParams('jobs.detail.monthlyFrom', {
          amount: formatMoney(monthlySalaryMin)
        });
  }
  if (monthlySalaryMin === undefined) return formatMoney(monthlySalaryMax);
  return `${getTrans(
    salaryQualifierKey({ monthlySalaryMin, monthlySalaryMax })
  )} ${formatMoney(monthlySalaryMax)}`;
};

/** The hourly rate and the monthly figure; whichever is missing stays out, and with neither so does the section. */
const Wage = ({ listing }: { listing: JobDetails }) => {
  const { hourlyRate } = listing;
  const monthly = monthlyFigureOf(listing);
  if (hourlyRate === undefined && monthly === undefined) return null;
  return (
    <Section title={getTrans('jobs.detail.wage')} testId="job-detail-wage">
      <SalaryInfo />
      {hourlyRate !== undefined && (
        <>
          <Row
            label={getTrans('jobs.detail.hourlyRate')}
            value={formatMoney(hourlyRate)}
          />
          <RaisedRow listing={{ ...listing, hourlyRate }} />
        </>
      )}
      {monthly !== undefined && (
        <Row label={getTrans('jobs.detail.monthlySalary')} value={monthly} />
      )}
    </Section>
  );
};

const SalaryInfo = () => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      mb: 1,
      '&:before': { display: 'none' },
      bgcolor: 'action.hover',
      borderRadius: 2
    }}
  >
    <AccordionSummary
      expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
    >
      <Text variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
        {getTrans('jobs.detail.salaryInfo')}
      </Text>
    </AccordionSummary>
    <AccordionDetails>
      <Text variant="body2" color="text.primary">
        {getTrans('jobs.detail.salaryInfoText')}
      </Text>
    </AccordionDetails>
  </Accordion>
);

/** The weekly hours as "20 Std./Woche", from whichever end of the range is known. */
const weeklyHoursOf = (listing: JobDetails): string | undefined => {
  const min = listing.minWeeklyHourMinutes ?? listing.maxWeeklyHourMinutes;
  const max = listing.maxWeeklyHourMinutes ?? listing.minWeeklyHourMinutes;
  // Unknown, or 0 at both ends, is no figure; a range from 0 ("0 - 20") still is one.
  if (min === undefined || max === undefined || (!min && !max))
    return undefined;
  return getTransWithParams('jobs.card.hoursPerWeek', {
    hours: hoursLabel(min, max)
  });
};

/** The working pattern, the days and times, the weekly and daily hours and the weekend; with none of it, no section. */
const Hours = ({ listing }: { listing: JobDetails }) => {
  const fixed = listing.workingTimeMode === 'FIXED';
  const windows = fixed ? groupWindows(listing.windows ?? []) : [];
  const days = !fixed ? listing.workingDays ?? [] : [];
  const times =
    !fixed && listing.workingTimeFrom && listing.workingTimeTo
      ? getTransWithParams('jobs.detail.times', {
          from: formatClock(listing.workingTimeFrom),
          to: formatClock(listing.workingTimeTo)
        })
      : undefined;
  const weekly = weeklyHoursOf(listing);
  const perDay = listing.minHoursPerDayMinutes;
  const hasAny =
    !!listing.workingTimeMode ||
    windows.length > 0 ||
    days.length > 0 ||
    !!times ||
    !!weekly ||
    !!perDay ||
    !!weekendDaysLabel(listing.weekendWork);
  if (!hasAny) return null;

  return (
    <Section title={getTrans('jobs.detail.hours')} testId="job-detail-hours">
      <WorkingTimeNotice listing={listing} />
      {windows.map(group => (
        <Row
          key={group.days.join('-')}
          label={dayRangeLabel(group.days)}
          value={getTransWithParams('jobs.detail.times', {
            from: formatClock(group.start),
            to: formatClock(group.end)
          })}
        />
      ))}
      {days.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {days.map(day => (
            <Chip
              key={day}
              size="small"
              label={getTrans(`jobs.filters.day.${day}` as TranslationKey)}
            />
          ))}
        </Box>
      )}
      {times && <Row label={getTrans('jobs.detail.hours')} value={times} />}
      {weekly && (
        <Row label={getTrans('jobs.detail.weeklyHours')} value={weekly} />
      )}
      {!!perDay && (
        <Row
          label={getTrans('jobs.detail.hoursPerDay')}
          value={getTransWithParams('jobs.filters.hoursOption', {
            hours: String(perDay / 60)
          })}
        />
      )}
      <WeekendWorkBlock weekend={listing.weekendWork} />
    </Section>
  );
};

/** The places, or the one place, how the work is placed, and the location check; nothing known, no section. */
const Where = ({ listing }: { listing: JobDetails }) => {
  const hasPoint =
    listing.latitude !== undefined && listing.longitude !== undefined;
  const places = listing.workLocations ?? [];
  const mode = listing.workplaceMode;
  const modeNote = mode === 'HYBRID' || (mode === 'REMOTE' && !listing.remote);
  const hasPlace =
    hasPoint ||
    !!listing.remote ||
    !!listing.locationName ||
    !!listing.locationCity;
  if (
    !places.length &&
    !hasPlace &&
    !modeNote &&
    listing.locationCheck !== 'GEOFENCE'
  )
    return null;
  return (
    <Section
      title={
        places.length
          ? getTransWithParams('jobs.detail.locations', {
              count: places.length
            })
          : getTrans('jobs.detail.location')
      }
      testId="job-detail-location"
    >
      {modeNote && (
        <Prose
          text={getTrans(`jobs.detail.workplace.${mode}` as TranslationKey)}
        />
      )}
      {places.length > 0 && <Locations listing={listing} />}
      <LocationCheckNotice listing={listing} />
      {places.length === 0 && hasPlace && (
        <WherePoint listing={listing} hasPoint={hasPoint} />
      )}
    </Section>
  );
};

/** One place, as before PJL: the map tile (only with a point to show), the name or city, and a link to a maps app. */
const WherePoint = ({
  listing,
  hasPoint
}: {
  listing: JobDetails;
  hasPoint: boolean;
}) => {
  return (
    <>
      {hasPoint && (
        <Box
          aria-hidden="true"
          data-testid="job-detail-map-tile"
          sx={{
            height: 96,
            borderRadius: 2,
            bgcolor: 'action.hover',
            mb: 1,
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <Iconify
            icon="solar:map-point-bold"
            width={28}
            sx={{ color: 'text.disabled' }}
          />
        </Box>
      )}
      {listing.remote && <Prose text={getTrans('jobs.card.remote')} />}
      {listing.locationName && <Prose text={listing.locationName} />}
      {!listing.locationName && listing.locationCity && (
        <Prose text={listing.locationCity} />
      )}
      {hasPoint && (
        <Link
          href={`https://maps.google.com/?q=${listing.latitude},${listing.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ fontWeight: 600 }}
        >
          {getTrans('jobs.detail.openInMaps')}
        </Link>
      )}
    </>
  );
};

const DetailSkeleton = () => (
  <Box data-testid="job-detail-skeleton">
    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 4, mb: 2 }} />
    <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4 }} />
  </Box>
);

/** The job part of an application's page while its listing loads: the sections' room, under the application. */
export const JobListingSectionsSkeleton = () => (
  <Box data-testid="job-detail-sections-skeleton" sx={{ mt: 1 }}>
    <Skeleton variant="text" width="40%" height={28} />
    <Skeleton variant="rounded" height={96} sx={{ borderRadius: 2, mb: 2 }} />
    <Skeleton variant="text" width="30%" height={28} />
    <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
  </Box>
);

/**
 * Who and what: the company (or the occupation where the channel hides it), its mark, the title and the badges.
 * `badges` go first in the badge row: where the talent's application stands, or since when my job runs.
 */
export const JobListingHeader = ({
  listing,
  badges
}: {
  listing: JobDetails;
  badges?: ReactNode;
}) => {
  const who = listing.companyName ?? listing.occupationName;
  return (
    <Card
      data-testid="job-detail-header"
      sx={{ borderRadius: 4, boxShadow: 'none', p: 2.5, mb: 3 }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {who && (
            <Text
              variant="subtitle1"
              component="p"
              color="text.primary"
              sx={{ fontWeight: 700 }}
            >
              {who}
            </Text>
          )}
          {listing.occupationName && (
            <Chip
              size="small"
              label={listing.occupationName.toUpperCase()}
              sx={{ mt: 0.5, fontWeight: 700 }}
            />
          )}
        </Box>
        {who && (
          <Box
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'action.hover',
              fontSize: 12,
              fontWeight: 700,
              color: 'primary.main'
            }}
          >
            {initialsOf(who)}
          </Box>
        )}
      </Box>
      {listing.title && (
        <Text
          variant="h5"
          component="h1"
          color="text.primary"
          sx={{ fontWeight: 800, mt: 1.5 }}
        >
          {listing.title}
        </Text>
      )}
      <Box sx={{ mt: 1.5 }}>
        <ListingBadges card={listing} leading={badges} />
      </Box>
    </Card>
  );
};

/**
 * Everything under the header: what the job is and asks for first, then contract, wage, hours, places, the interview
 * and good to know. On an application's page they come under where the talent stands. No process and no apply footer: the job details page adds both. The page of an application shows where
 * the talent stands in the process on top already, my job is past it.
 */
export const JobListingSections = ({ listing }: { listing: JobDetails }) => {
  // What pro-core adds by itself after the operator's own list: a PRO CV, location sharing for a geofence check.
  const autoRequirements = listing.autoRequirements ?? [];
  // The editor stores an emptied field as "<p><br></p>": that is no text, and its section stays out.
  const description = hasListingText(listing.description)
    ? listing.description
    : undefined;
  const requirements = hasListingText(listing.requirements)
    ? listing.requirements
    : undefined;
  const interviewInfo = hasListingText(listing.interviewInfo)
    ? listing.interviewInfo
    : undefined;
  const goodToKnow = [listing.goodToKnow, listing.otherInformation].find(
    hasListingText
  );

  return (
    <>
      {description && (
        <Section title={getTrans('jobs.detail.description')}>
          <ListingText text={description} />
        </Section>
      )}
      {(requirements || autoRequirements.length > 0) && (
        <Section
          title={getTrans('jobs.detail.requirements')}
          testId="job-detail-requirements"
        >
          {requirements && <ListingText text={requirements} />}
          {autoRequirements.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {autoRequirements.map(requirement => (
                <Text
                  key={requirement}
                  variant="body2"
                  component="li"
                  color="text.primary"
                >
                  {getTrans(
                    `jobs.detail.autoRequirement.${requirement}` as TranslationKey
                  )}
                </Text>
              ))}
            </Box>
          )}
        </Section>
      )}
      <Contract listing={listing} />
      <Wage listing={listing} />
      <Hours listing={listing} />
      <Where listing={listing} />
      {interviewInfo && (
        <Section title={getTrans('jobs.detail.interview')}>
          <ListingText text={interviewInfo} />
        </Section>
      )}
      {goodToKnow && (
        <Section title={getTrans('jobs.detail.usefulToKnow')}>
          <ListingText text={goodToKnow} />
        </Section>
      )}
    </>
  );
};

export const JobListingDetail = ({ refId }: { refId: string }) => {
  const { listing, isLoading, isNotFound, isUnavailable, isError } =
    useJobListing(refId);
  const record = useListingEngagement(refId, 'detail');

  useEffect(() => {
    if (listing) record('OPENED');
  }, [listing, record]);

  if (isLoading) return <DetailSkeleton />;
  if (isNotFound) {
    return (
      <NotFoundErrorScreen
        title={getTrans('jobs.detail.gone.title')}
        description1={getTrans('jobs.detail.gone.body1')}
        description2={getTrans('jobs.detail.gone.body2')}
      />
    );
  }
  if (isUnavailable) {
    return (
      <NotFoundErrorScreen
        title={getTrans('jobs.unavailable.title')}
        description1={getTrans('jobs.unavailable.body1')}
        description2={getTrans('jobs.unavailable.body2')}
      />
    );
  }
  if (isError || !listing) {
    return (
      <NotFoundErrorScreen
        title={getTrans('jobs.error.title')}
        description1={getTrans('jobs.error.body1')}
        description2={getTrans('jobs.error.body2')}
      />
    );
  }

  return (
    <Box>
      <JobListingHeader listing={listing} />
      <JobListingSections listing={listing} />
      {(listing.processSteps?.length ?? 0) > 0 && (
        <Section title={getTrans('jobs.detail.process')}>
          <ProcessSteps listing={listing} />
        </Section>
      )}
      {/* The foot of the page is fixed above the navigation; the last section keeps clear of it. */}
      <Box data-testid="job-listing-apply-seam" sx={{ height: 160 }} />
      <ApplyFooter listing={listing} />
    </Box>
  );
};
